import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { SprayCan } from "lucide-react";
import Calculator from "@/pages/Calculator";
import Customers from "@/pages/Customers";
import Quotes from "@/pages/Quotes";
import Login from "@/pages/Login";
import PublicSite from "@/pages/PublicSite";
import Backup from "@/pages/Backup";
import Invoices from "@/pages/Invoices";
import InvoiceImport from "@/pages/InvoiceImport";
import Workshop from "@/pages/Workshop";
import CalendarPage from "@/pages/CalendarPage";
import ClientPage from "@/pages/ClientPage";
import CustomerSOA from "@/pages/CustomerSOA";
import ARDashboard from "@/pages/ARDashboard";
import Dashboard from "@/pages/Dashboard";
import PurchasesHome from "@/pages/purchases/PurchasesHome";
import CaptureBill from "@/pages/purchases/CaptureBill";
import BillsList from "@/pages/purchases/BillsList";
import BillDetail from "@/pages/purchases/BillDetail";
import Suppliers from "@/pages/purchases/Suppliers";
import SupplierStatement from "@/pages/purchases/SupplierStatement";
import SupplierSOAList from "@/pages/purchases/SupplierSOAList";
import SupplierSOACapture from "@/pages/purchases/SupplierSOACapture";
import SupplierSOADetail from "@/pages/purchases/SupplierSOADetail";
import BillDuplicates from "@/pages/purchases/BillDuplicates";
import ExportPage from "@/pages/purchases/ExportPage";
import StoragePage from "@/pages/purchases/StoragePage";
import PnL from "@/pages/PnL";
import Reports from "@/pages/Reports";
import ClaimsDashboard from "@/pages/claims/ClaimsDashboard";
import ClaimsList from "@/pages/claims/ClaimsList";
import ClaimNew from "@/pages/claims/ClaimNew";
import ClaimDetail from "@/pages/claims/ClaimDetail";
import ClaimLumpSumEditor from "@/pages/claims/ClaimLumpSumEditor";
import Insurers from "@/pages/claims/Insurers";
import CatalogueHome from "@/pages/inventory/CatalogueHome";
import ItemNew from "@/pages/inventory/ItemNew";
import ItemDetail from "@/pages/inventory/ItemDetail";
import OrderList from "@/pages/inventory/OrderList";
import InventorySuppliers from "@/pages/inventory/InventorySuppliers";
import RecurringBills from "@/pages/purchases/RecurringBills";
import WorkerLogin from "@/pages/freelance/WorkerLogin";
import WorkerPortal from "@/pages/freelance/WorkerPortal";
import FreelanceDashboard from "@/pages/freelance/FreelanceDashboard";
import FreelanceWorkers from "@/pages/freelance/FreelanceWorkers";
import FreelanceRecords from "@/pages/freelance/FreelanceRecords";
import FreelancePayments from "@/pages/freelance/FreelancePayments";
import FreelanceStatements from "@/pages/freelance/FreelanceStatements";
import WorkerInvoices from "@/pages/freelance/WorkerInvoices";
import WorkerInvoiceEditor from "@/pages/freelance/WorkerInvoiceEditor";
import HRDashboard from "@/pages/hr/HRDashboard";
import HREmployees from "@/pages/hr/HREmployees";
import HREmployeeDetail from "@/pages/hr/HREmployeeDetail";
import HRLeave from "@/pages/hr/HRLeave";
import HRAttendance from "@/pages/hr/HRAttendance";
import HRDocuments from "@/pages/hr/HRDocuments";
import HRReports from "@/pages/hr/HRReports";

function Protected({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) {
    return (
      <div className="min-h-screen workshop-bg flex items-center justify-center text-slate-400" data-testid="auth-loading">
        <SprayCan className="w-6 h-6 animate-pulse mr-2 text-amber-400" /> Checking session…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  if (user.role === "freelance_worker") return <Navigate to="/my" replace />; // workers never see workshop pages
  return children;
}

function WorkerProtected({ children }) {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen workshop-bg flex items-center justify-center text-slate-400" data-testid="auth-loading"><SprayCan className="w-6 h-6 animate-pulse mr-2 text-amber-400" /> Checking session…</div>;
  if (!user) return <Navigate to="/worker-login" replace />;
  if (user.role !== "freelance_worker") return <Navigate to="/dashboard" replace />;
  return children;
}

// HR: owner, "hr" role, or a user explicitly granted HR access. Backend enforces the same rule on /api/hr/*.
function HRProtected({ children }) {
  const { user } = useAuth();
  return <Protected>{user && !user.hr_access ? <Navigate to="/dashboard" replace /> : children}</Protected>;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<PublicSite />} />
            <Route path="/login" element={<Login />} />
            <Route path="/app" element={<Protected><Customers /></Protected>} />
            <Route path="/quote" element={<Protected><Calculator /></Protected>} />
            <Route path="/quotes" element={<Protected><Quotes /></Protected>} />
            <Route path="/backup" element={<Protected><Backup /></Protected>} />
            <Route path="/invoices" element={<Protected><Invoices /></Protected>} />
            <Route path="/invoices/import" element={<Protected><InvoiceImport /></Protected>} />
            <Route path="/workshop" element={<Protected><Workshop /></Protected>} />
            <Route path="/calendar" element={<Protected><CalendarPage /></Protected>} />
            <Route path="/customers/:id" element={<Protected><ClientPage /></Protected>} />
            <Route path="/customers/:id/soa" element={<Protected><CustomerSOA /></Protected>} />
            <Route path="/ar" element={<Protected><ARDashboard /></Protected>} />
            <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
            <Route path="/purchases" element={<Protected><PurchasesHome /></Protected>} />
            <Route path="/purchases/capture" element={<Protected><CaptureBill /></Protected>} />
            <Route path="/purchases/bills" element={<Protected><BillsList /></Protected>} />
            <Route path="/purchases/duplicates" element={<Protected><BillDuplicates /></Protected>} />
            <Route path="/purchases/statements" element={<Protected><SupplierSOAList /></Protected>} />
            <Route path="/purchases/statements/new" element={<Protected><SupplierSOACapture /></Protected>} />
            <Route path="/purchases/statements/:id" element={<Protected><SupplierSOADetail /></Protected>} />
            <Route path="/purchases/recurring" element={<Protected><RecurringBills /></Protected>} />
            <Route path="/worker-login" element={<WorkerLogin />} />
            <Route path="/my/*" element={<WorkerProtected><WorkerPortal /></WorkerProtected>} />
            <Route path="/freelance" element={<Protected><FreelanceDashboard /></Protected>} />
            <Route path="/freelance/workers" element={<Protected><FreelanceWorkers /></Protected>} />
            <Route path="/freelance/records" element={<Protected><FreelanceRecords /></Protected>} />
            <Route path="/freelance/payments" element={<Protected><FreelancePayments /></Protected>} />
            <Route path="/freelance/statements" element={<Protected><FreelanceStatements /></Protected>} />
            <Route path="/freelance/client-invoices" element={<Protected><WorkerInvoices /></Protected>} />
            <Route path="/freelance/client-invoices/new" element={<Protected><WorkerInvoiceEditor /></Protected>} />
            <Route path="/freelance/client-invoices/:invId" element={<Protected><WorkerInvoiceEditor /></Protected>} />
            <Route path="/hr" element={<HRProtected><HRDashboard /></HRProtected>} />
            <Route path="/hr/employees" element={<HRProtected><HREmployees /></HRProtected>} />
            <Route path="/hr/employees/:id" element={<HRProtected><HREmployeeDetail /></HRProtected>} />
            <Route path="/hr/leave" element={<HRProtected><HRLeave /></HRProtected>} />
            <Route path="/hr/attendance" element={<HRProtected><HRAttendance /></HRProtected>} />
            <Route path="/hr/documents" element={<HRProtected><HRDocuments /></HRProtected>} />
            <Route path="/hr/reports" element={<HRProtected><HRReports /></HRProtected>} />
            <Route path="/purchases/bills/:id" element={<Protected><BillDetail /></Protected>} />
            <Route path="/purchases/suppliers" element={<Protected><Suppliers /></Protected>} />
            <Route path="/purchases/suppliers/:id" element={<Protected><SupplierStatement /></Protected>} />
            <Route path="/purchases/export" element={<Protected><ExportPage /></Protected>} />
            <Route path="/purchases/storage" element={<Protected><StoragePage /></Protected>} />
            <Route path="/pnl" element={<Protected><PnL /></Protected>} />
            <Route path="/reports" element={<Protected><Reports /></Protected>} />
            <Route path="/claims" element={<Protected><ClaimsDashboard /></Protected>} />
            <Route path="/claims/list" element={<Protected><ClaimsList /></Protected>} />
            <Route path="/claims/new" element={<Protected><ClaimNew /></Protected>} />
            <Route path="/claims/insurers" element={<Protected><Insurers /></Protected>} />
            <Route path="/claims/:id" element={<Protected><ClaimDetail /></Protected>} />
            <Route path="/claims/:id/lumpsum/new" element={<Protected><ClaimLumpSumEditor /></Protected>} />
            <Route path="/claims/:id/lumpsum/:invId" element={<Protected><ClaimLumpSumEditor /></Protected>} />
            <Route path="/inventory" element={<Protected><CatalogueHome /></Protected>} />
            <Route path="/inventory/new" element={<Protected><ItemNew /></Protected>} />
            <Route path="/inventory/items/:id" element={<Protected><ItemDetail /></Protected>} />
            <Route path="/inventory/order-list" element={<Protected><OrderList /></Protected>} />
            <Route path="/inventory/suppliers" element={<Protected><InventorySuppliers /></Protected>} />
            <Route path="/inventory/suppliers/:id" element={<Protected><InventorySuppliers /></Protected>} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
      <Toaster position={window.matchMedia("(max-width: 767px)").matches ? "bottom-center" : "bottom-right"} richColors theme="dark" duration={3000} visibleToasts={2} closeButton
        toastOptions={{ classNames: { toast: "pointer-events-auto", closeButton: "!bg-slate-800 !border-slate-600 !text-slate-200" } }} />
    </div>
  );
}

export default App;
